import React from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  GitHub,
  LinkedIn,
  Email,
  Launch,
} from '@mui/icons-material';
import { X } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';

const Footer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Product',
      links: [
        { label: 'Features', path: '/#features', isHashLink: true },
        { label: 'Security', path: '/#security', isHashLink: true },
        { label: 'Security Tools', path: '/tools' },
        { label: 'Pricing', path: '/pricing' },
        { label: 'Download', path: '/download' },
        { label: 'Changelog', path: 'https://github.com/KiefStudioMA/PWGen-Rust/releases', external: true },
      ]
    },
    {
      title: 'Resources',
      links: [
        { label: 'Documentation', path: '/docs' },
        { label: 'Blog', path: '/blog' },
        { label: 'Security Tools', path: '/tools' },
        { label: 'README Guide', path: 'https://github.com/KiefStudioMA/PWGen-Rust#readme', external: true },
        { label: 'Downloads', path: '/download' },
      ]
    },
    {
      title: 'Community',
      links: [
        { label: 'GitHub', path: 'https://github.com/KiefStudioMA/PWGen-Rust', external: true },
        { label: 'Issues & Support', path: 'https://github.com/KiefStudioMA/PWGen-Rust/issues', external: true },
        { label: 'Contributing', path: 'https://github.com/KiefStudioMA/PWGen-Rust#contributing', external: true },
        { label: 'Contact', path: '/contact' },
      ]
    },
  ];

  const handleHashNavigation = (path) => {
    const [routePath, hash] = path.split('#');
    if (location.pathname !== routePath) {
      // Navigate to the page first, then scroll with retry mechanism
      navigate(routePath);
      
      // Retry mechanism to ensure element exists before scrolling
      const scrollToElement = (retries = 0) => {
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (retries < 10) {
            // Retry with exponential backoff: 100ms, 200ms, 400ms, etc.
            setTimeout(() => scrollToElement(retries + 1), 100 * Math.pow(1.5, retries));
          }
        }
      };
      
      // Start the retry process
      setTimeout(() => scrollToElement(), 100);
    } else {
      // Already on the page, just scroll
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const socialLinks = [
    { icon: <GitHub />, url: 'https://github.com/KiefStudioMA/PWGen-Rust', label: 'GitHub' },
    { icon: <X />, url: 'https://x.com/Kief_ma', label: 'X (Twitter)' },
    { icon: <LinkedIn />, url: 'https://www.linkedin.com/company/kief-studio/', label: 'LinkedIn' },
    { icon: <Email />, url: 'mailto:contact@kief.studio', label: 'Email' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? '#0a0e1a' : '#f8fafc',
        borderTop: '1px solid',
        borderColor: 'divider',
        mt: 'auto',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.accent?.main || '#00695c'}, transparent)`,
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }}>
            {/* Logo and Description */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ mb: 4 }}>
                <Box
                  component="img"
                  src={theme.palette.mode === 'dark' 
                    ? "/assets/images/PWGenLogo-Dark-Wide.png" 
                    : "/assets/images/PWGenLogo-Wide.png"
                  }
                  alt="PWGen-Rust"
                  sx={{
                    height: 48,
                    mb: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                />
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 4, 
                    lineHeight: 1.7,
                    fontSize: '1rem',
                    maxWidth: 400,
                  }}
                >
                  Enterprise-grade password manager built with Rust. Zero-knowledge architecture 
                  with military-grade encryption for complete control over your digital security.
                </Typography>
                
                {/* Social Links */}
                <Stack direction="row" spacing={2}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      sx={{
                        color: 'text.secondary',
                        backgroundColor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: theme.palette.accent?.main || '#00695c',
                          backgroundColor: alpha(theme.palette.accent?.main || '#00695c', 0.08),
                          borderColor: theme.palette.accent?.main || '#00695c',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Stack>
                
                {/* Snap Store Badges */}
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Link
                    href="https://snapcraft.io/pwgen-rust"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={theme.palette.mode === 'dark' 
                        ? "https://snapcraft.io/en/light/install.svg" 
                        : "https://snapcraft.io/en/dark/install.svg"
                      }
                      alt="Get it from the Snap Store"
                      sx={{
                        height: 40,
                        width: 'auto',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                        },
                      }}
                    />
                  </Link>
                </Stack>
              </Box>
            </Grid>

            {/* Footer Links */}
            {footerSections.map((section, sectionIndex) => (
              <Grid size={{ xs: 4, sm: 4, md: 2 }} key={sectionIndex}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      fontSize: '1.1rem',
                      color: 'text.primary',
                      position: 'relative',
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Stack spacing={2}>
                    {section.links.map((link, linkIndex) => (
                      <Link
                        key={linkIndex}
                        component={link.external ? 'a' : (link.isHashLink ? 'div' : RouterLink)}
                        to={!link.external && !link.isHashLink ? link.path : undefined}
                        href={link.external ? link.path : undefined}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        onClick={link.isHashLink ? (e) => {
                          e.preventDefault();
                          handleHashNavigation(link.path);
                        } : undefined}
                        sx={{
                          color: 'text.secondary',
                          textDecoration: 'none',
                          fontSize: '0.925rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            color: theme.palette.accent?.main || '#00695c',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          {link.label}
                        </Box>
                        {link.external && (
                          <Launch 
                            sx={{ 
                              fontSize: 14, 
                              opacity: 0.6,
                              transition: 'opacity 0.2s ease'
                            }} 
                          />
                        )}
                      </Link>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 0 }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'center' },
            gap: 3,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          {/* Copyright */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              © {currentYear} <Link 
                href="https://kief.studio" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ 
                  color: theme.palette.accent?.main || '#00695c', 
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Kief Studio
              </Link>. All rights reserved.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PWGen-Rust - Built with ❤️ and <Box component="span" sx={{ color: theme.palette.accent?.main || '#00695c', fontWeight: 600 }}>Rust</Box> for security professionals.
            </Typography>
          </Box>

          {/* Legal Links */}
          <Stack
            direction="row"
            spacing={3}
            divider={<Typography color="text.disabled" sx={{ fontSize: '0.8rem' }}>•</Typography>}
            sx={{
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
            }}
          >
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              Privacy
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              Terms
            </Link>
            <Link
              component={RouterLink}
              to="/sla"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              SLA
            </Link>
            <Link
              component={RouterLink}
              to="/disclaimer"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              Disclaimer
            </Link>
            <Link
              component={RouterLink}
              to="/licensing"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              Licensing
            </Link>
            <Link
              component={RouterLink}
              to="/cookie-preferences"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: theme.palette.accent?.main || '#00695c',
                },
              }}
            >
              Cookie Settings
            </Link>
          </Stack>
        </Box>
        
        {/* Kief Studio Logo Section */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 3,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Link
            href="https://kief.studio"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              opacity: 0.7,
              '&:hover': {
                opacity: 1,
                transform: 'scale(1.05)',
              },
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'dark' 
                ? "/img/KS-Logo-Full-Light.png" 
                : "/img/KS-Logo-Full-Dark.png"
              }
              alt="Kief Studio"
              sx={{
                height: { xs: 20, sm: 24 },
                width: 'auto',
                transition: 'all 0.3s ease',
              }}
            />
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;